import React, { useState, useRef, useEffect } from 'react';
import useDidMountEffect from '@use-effect/did-update';
import useSticky from 'Common/hooks/useSticky';

import Grid, { Col } from 'Components/Grid';

import {
    TabsWrapper,
    OverflowContainer,
    Tabs,
    Tab,
    DownIcon,
    TabIndicator,
    Panel
} from './TabNavigation.styled';

interface Tab {
    label: string;
    id: string;
    content: React.ReactNode | string;
}

export interface Props {
    tabs: Tab[];
    sticky?: boolean;
}

const Tabnavigation: React.FC<Props> = props => {
    const { tabs, sticky = true } = props;

    const [activeTabIndex, setActiveTabIndex] = useState(0);

    const { ref, isSticky, Sticky, stickyProps } = useSticky<HTMLDivElement>({ disabled: !sticky });
    const tabsRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleTabKeydown = (event: React.KeyboardEvent) => {
        if (tabsRef.current && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
            const tabsEl = tabsRef.current;
            let newIndex: number;

            if (event.key === 'ArrowLeft') {
                newIndex = activeTabIndex - 1 < 0 ? tabs.length - 1 : activeTabIndex - 1;
            } else {
                newIndex = activeTabIndex + 1 > tabs.length - 1 ? 0 : activeTabIndex + 1;
            }

            (tabsEl.children[newIndex] as HTMLButtonElement).focus();
            setActiveTabIndex(newIndex);
        }
    };

    useEffect(() => {
        // Changes tab on intial load if hash is set and valid
        if (location.hash && location.hash !== '') {
            const hash = location.hash.replace('#', '');
            const activeTabIndex = tabs.findIndex(tab => tab.id === hash);

            if (activeTabIndex > 0) {
                setActiveTabIndex(activeTabIndex);
            }
        }
    }, []);

    useDidMountEffect(() => {
        history.replaceState(undefined, '', `#${tabs[activeTabIndex].id}`);

        if (containerRef.current && containerRef.current.getBoundingClientRect().top < 0) {
            //added +1 to stop stickynav from deploying
            window.scrollBy({ top: containerRef.current.getBoundingClientRect().top + 1 });
        }
    }, [activeTabIndex]);

    return (
        <div ref={containerRef}>
            <Sticky {...stickyProps}>
                <TabsWrapper ref={ref} $isSticky={isSticky} data-testid="tabsWrapper">
                    <Grid>
                        <Col>
                            <OverflowContainer>
                                <Tabs
                                    role="tablist"
                                    aria-label="Course information tabs"
                                    ref={tabsRef}
                                >
                                    {tabs.map((tab, index) => (
                                        <Tab
                                            id={`tab-${tab.id}`}
                                            key={`tab-${tab.id}`}
                                            role="tab"
                                            aria-selected={index === activeTabIndex}
                                            aria-controls={`panel-${tab.id}`}
                                            tabIndex={index === activeTabIndex ? 0 : -1}
                                            $isActive={index === activeTabIndex}
                                            onClick={() => setActiveTabIndex(index)}
                                            onKeyDown={handleTabKeydown}
                                        >
                                            {tab.label}
                                            <DownIcon />
                                        </Tab>
                                    ))}
                                    <TabIndicator $activeTabIndex={activeTabIndex} />
                                </Tabs>
                            </OverflowContainer>
                        </Col>
                    </Grid>
                </TabsWrapper>
            </Sticky>
            {tabs.map((tab, index) => (
                <Panel
                    id={`panel-${tab.id}`}
                    key={`panel-${tab.id}`}
                    role="tabpanel"
                    tabIndex={0}
                    aria-labelledby={`tab-${tab.id}`}
                    hidden={activeTabIndex !== index}
                >
                    {tab.content}
                </Panel>
            ))}
        </div>
    );
};

export default Tabnavigation;
